import { ReactNode, useState, useEffect } from "react";

// Base input styling class
const INPUT_BASE_CLASSES = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100";

export interface FormFieldProps {
  label: string;
  children: ReactNode;
  required?: boolean;
  error?: string;
  helpText?: string;
}

export function FormField({ label, children, required, error, helpText }: FormFieldProps) {
  return (
    <div>
      <label className="block text-sm font-subheader text-gray-700 dark:text-gray-300 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
      {helpText && !error && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helpText}</p>
      )}
    </div>
  );
}

export interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
  placeholder?: string;
  step?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  error?: boolean;
}

export function NumberInput({ 
  value, 
  onChange, 
  placeholder, 
  step = 1, 
  min, 
  max, 
  disabled = false,
  error = false 
}: NumberInputProps) {
  const errorClasses = error 
    ? "border-red-300 dark:border-red-600 focus:ring-red-500 focus:border-red-500" 
    : "";
  
  return (
    <input
      type="number"
      value={value || ""}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      placeholder={placeholder}
      step={step}
      min={min}
      max={max}
      disabled={disabled}
      className={`${INPUT_BASE_CLASSES} ${errorClasses} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    />
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: readonly SelectOption[] | readonly string[] | SelectOption[] | string[];
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
}

export function Select({ 
  value, 
  onChange, 
  options, 
  disabled = false, 
  error = false,
  placeholder 
}: SelectProps) {
  const errorClasses = error 
    ? "border-red-300 dark:border-red-600 focus:ring-red-500 focus:border-red-500" 
    : "";

  // Handle both string[] and SelectOption[] formats
  const normalizedOptions = options.map(option => 
    typeof option === 'string' 
      ? { value: option, label: option }
      : option
  );

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={`${INPUT_BASE_CLASSES} ${errorClasses} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {normalizedOptions.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export interface TextInputProps {
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'email' | 'tel' | 'password';
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  readOnly?: boolean;
}

export function TextInput({
  value,
  onChange,
  type = 'text',
  placeholder,
  disabled = false,
  error = false,
  readOnly = false
}: TextInputProps) {
  const errorClasses = error 
    ? "border-red-300 dark:border-red-600 focus:ring-red-500 focus:border-red-500" 
    : "";
  
  const readOnlyClasses = readOnly 
    ? "bg-gray-50 dark:bg-gray-600 cursor-not-allowed" 
    : "";

  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      className={`${INPUT_BASE_CLASSES} ${errorClasses} ${readOnlyClasses} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    />
  );
}
